import { defineStore } from "pinia";
import { defaultApi } from "@/api";
import { FEATURE_NAMES, METRIC_TYPE, logError, logEvent, logInfo, logMeasurement } from "@/loggers";
import type { PartnerRelationship } from "./types";
import { useUserStore } from "@/store/user/user";
import i18n from "@/i18n";

const API_PATH = "/isce/tenant/customer/parents";

// Cached collator avoids per-comparison Collator instantiation.
const collator = new Intl.Collator(undefined, { sensitivity: "base" });

// Module-level dedup variable — request state is not app state.
let inFlight: Promise<void> | null = null;

export const usePartnerRelationshipsStore = defineStore("partnerRelationships", {
  state: () => ({
    partners: [] as PartnerRelationship[]
  }),

  getters: {
    customerOptions: (state): { label: string; value: string }[] => {
      const { user } = useUserStore();
      const ownCode = user?.customer?.customerCode;
      const ownOption = ownCode ? [{ label: i18n.global.t("OWN_SHIPMENTS"), value: ownCode }] : [];
      const partnerOptions = [...state.partners]
        .map((p) => ({ label: p.partnerCustomerName, value: p.partnerCustomerCode }))
        .sort((a, b) => collator.compare(a.label, b.label));
      return [...ownOption, ...partnerOptions];
    },

    companyNamesByCode: (state): Map<string, string> => {
      const map = new Map(state.partners.map((p) => [p.partnerCustomerCode, p.partnerCustomerName]));
      const { user } = useUserStore();
      const ownCode = user?.customer?.customerCode;
      const ownName = user?.customer?.customerName;
      if (ownCode && ownName) map.set(ownCode, ownName);
      return map;
    }
  },

  actions: {
    async fetch(forceRefresh = false): Promise<void> {
      if (!forceRefresh && this.partners.length > 0) return;
      if (inFlight) return inFlight;

      const apiStart = performance.now();
      inFlight = (async () => {
        try {
          const res = await defaultApi.apiUnversioned.partnerRelationships.getAll();

          if (!res || res.status >= 400) {
            const synthError: any = new Error(`HTTP ${res?.status ?? "no-response"}`);
            synthError.response = res;
            throw synthError;
          }

          this.partners = Array.isArray(res.data) ? res.data : [];

          logInfo(
            FEATURE_NAMES.MVS_FILTERS,
            `Fetched customer parents successfully: status=${res.status}, count=${this.partners.length}`
          );
        } catch (err: any) {
          const statusCode = err?.response?.status ?? 0;
          const responseBody = err?.response?.data;
          const errorAttr =
            responseBody !== undefined && responseBody !== null
              ? JSON.stringify(responseBody)
              : err?.message || "Unknown error";

          logError(
            FEATURE_NAMES.MVS_FILTERS,
            `Error fetching customer parents: status=${statusCode}, error=${errorAttr}`
          );
          logEvent(FEATURE_NAMES.MVS_FILTERS, METRIC_TYPE.API_CALL_FAILURE, {
            api: API_PATH,
            statusCode,
            error: errorAttr
          });
        } finally {
          inFlight = null;
          logMeasurement(FEATURE_NAMES.MVS_FILTERS, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
            api: API_PATH
          });
        }
      })();

      return inFlight;
    },

    reset(): void {
      this.partners = [];
      inFlight = null;
    }
  }
});
